/**
 * Multi-Option Variant Picker
 * ============================
 * Handles products with multiple variant options (e.g., Jersey Type + Limited Number)
 * Manages option selection and finds matching variants
 */

// Guard against redeclaration during AJAX section swaps
if (typeof MultiOptionVariantPicker === 'undefined') {

class MultiOptionVariantPicker {
  constructor(element) {
    this.container = element;
    this.productId = this.container.dataset.productId;
    this.totalVariants = parseInt(this.container.dataset.totalVariants) || 0;

    // Parse variant data
    const variantDataEl = this.container.querySelector('[data-variant-data]');
    const variantDataJson = variantDataEl ? JSON.parse(variantDataEl.textContent) : {};
    this.variants = variantDataJson.variants || [];
    this.formatted = variantDataJson.formatted || {};
    this.config = variantDataJson.config || null;

    // State
    this.selectedOptions = {}; // Index -> value
    this.numberOfOptions = this.container.querySelectorAll('[data-option-group]').length;
    this.optionNames = []; // Store option names for schema lookup
    this.currentVariant = null;
    this.isInitializing = true; // Flag to prevent scroll during initialization
    this.isCombinedListingNavigation = false; // Flag to track combined listing navigation

    // Elements
    this.variantIdInput = this.container.querySelector('[data-variant-id-input]');
    this.summaryEl = this.container.querySelector('[data-selection-summary]');
    this.optionGroups = this.container.querySelectorAll('[data-option-group]');

    // Product section elements (for updating price, SKU, etc.)
    this.productSection = document.querySelector('.js-product');

    // Store original product title for dynamic updates
    this.titleElement = document.querySelector('.product-info__title h1, .product__title h1, [data-product-title]');
    this.originalTitle = this.titleElement ? this.titleElement.textContent.trim() : '';

    // Extract option names
    this.optionGroups.forEach(group => {
      const optionName = group.dataset.optionName;
      const optionIndex = parseInt(group.dataset.optionIndex);
      this.optionNames[optionIndex] = optionName;
    });

    this.init();
  }

  init() {
    if (this.config) this.validateConfig();

    this.container.addEventListener('click', (e) => {
      const button = e.target.closest('[data-option-button]');
      if (button && !button.disabled) {
        const group = button.closest('[data-option-group]');
        const optionIndex = parseInt(group.dataset.optionIndex);
        this.handleOptionSelect(button, optionIndex);
      }
    });

    // Setup search inputs
    this.optionGroups.forEach(group => {
      const optionIndex = parseInt(group.dataset.optionIndex);
      const searchInput = group.querySelector(`[data-search-input-${optionIndex}]`);
      if (searchInput) {
        searchInput.addEventListener('input', (e) => this.handleSearch(e, optionIndex));
      }
    });

    // Initialize combined listings pre-selected state
    this.initCombinedListingsState();

    // Initial state
    this.updateSummary();
    this.updateAddToCartButton(false);

    this.updateVariantAvailability();
    this.checkURLVariant();

    // Mark initialization as complete - allows scroll behavior after this point
    this.isInitializing = false;
  }

  /**
   * Initialize state for all option groups - detects pre-selected options
   * from Liquid (buttons marked with is-selected class or aria-pressed="true")
   * Works for BOTH combined listings AND regular products
   */
  initCombinedListingsState() {
    this.optionGroups.forEach(group => {
      const isCombinedListing = group.dataset.isCombinedListing === 'true';
      const initialValue = group.dataset.initialValue;
      const optionIndex = parseInt(group.dataset.optionIndex);

      // Process ALL option groups that have an initial value (not just combined listings)
      if (initialValue) {
        // Set the initial selected option
        this.selectedOptions[optionIndex] = initialValue;

        // Find the selected button - check both aria-pressed="true" AND .is-selected class
        let selectedButton = group.querySelector('[data-option-button][aria-pressed="true"]');
        if (!selectedButton) {
          selectedButton = group.querySelector('[data-option-button].is-selected');
        }

        // If no button is selected yet, find the one matching the initial value
        if (!selectedButton) {
          selectedButton = group.querySelector(`[data-option-button][data-option-value="${initialValue}"]`);
        }

        // Update the selected value display element
        const selectedValueEl = this.container.querySelector(`[data-selected-value-${optionIndex}]`);
        if (selectedValueEl) {
          // Use button's display value if found, otherwise use initial value from Liquid
          const displayValue = selectedButton
            ? (selectedButton.dataset.displayValue || selectedButton.dataset.optionValue)
            : initialValue;

          selectedValueEl.textContent = displayValue;
          selectedValueEl.classList.add('has-selection');
        }

        // Ensure the button is marked as selected (for combined listings where current product's button should be selected)
        if (selectedButton && isCombinedListing) {
          selectedButton.setAttribute('aria-pressed', 'true');
          selectedButton.classList.add('is-selected');
        }
      }
    });

    // After setting initial state, try to find matching variant
    if (Object.keys(this.selectedOptions).length > 0) {
      this.findAndSelectVariant();
    }
  }

  handleOptionSelect(button, optionIndex) {
    if (button.disabled) return;

    const optionValue = button.dataset.optionValue;
    const displayValue = button.dataset.displayValue || optionValue;

    // Check for Combined Listings product swap
    // Only swap if BOTH productUrl exists on button AND currentProductUrl exists on section
    // This prevents false swaps when the product section doesn't have data-product-url set
    const productUrl = button.dataset.productUrl;
    if (productUrl) {
      const currentProductUrl = this.productSection?.dataset.productUrl;
      
      // Normalize URLs for comparison (handle relative vs absolute, trailing slashes)
      const normalize = (url) => {
        if (!url) return '';
        try {
          // If full URL, use that. If relative, make absolute using current origin
          const fullUrl = url.startsWith('http') ? url : window.location.origin + url;
          const urlObj = new URL(fullUrl);
          return urlObj.pathname.replace(/\/$/, ''); // Remove trailing slash
        } catch (e) {
          return url;
        }
      };

      // Get current product handle for safety check
      // Assuming URL structure /products/handle
      const getHandle = (url) => {
        const parts = normalize(url).split('/');
        return parts[parts.length - 1];
      };

      const normProductUrl = normalize(productUrl);
      const normCurrentUrl = normalize(currentProductUrl || window.location.pathname);
      
      // Only swap if URLs are different AND product handle is different
      // This prevents infinite loops on the same product page
      if (normProductUrl !== normCurrentUrl) {
        const productHandle = getHandle(productUrl);
        const currentHandle = getHandle(currentProductUrl || window.location.pathname);
        
        if (productHandle !== currentHandle) {
          // This is a combined listing - swap to different product
          this.handleCombinedListingsSwap(productUrl, button);
          return;
        }
      }
    }

    // Update button states in this group
    const group = button.closest('[data-option-group]');
    group.querySelectorAll('[data-option-button]').forEach(btn => {
      btn.setAttribute('aria-pressed', 'false');
      btn.classList.remove('is-selected');
    });

    button.setAttribute('aria-pressed', 'true');
    button.classList.add('is-selected');

    // Clear selections AFTER this option (dependent options), but keep selections BEFORE
    for (let i = optionIndex + 1; i < this.numberOfOptions; i++) {
      delete this.selectedOptions[i];
      // Reset display text
      const selectedValueEl = this.container.querySelector(`[data-selected-value-${i}]`);
      if (selectedValueEl) {
        selectedValueEl.textContent = 'Not selected';
        selectedValueEl.classList.remove('has-selection');
      }
      // Unselect buttons
      const otherGroup = this.container.querySelector(`[data-option-group][data-option-index="${i}"]`);
      if (otherGroup) {
        otherGroup.querySelectorAll('[data-option-button]').forEach(btn => {
          btn.setAttribute('aria-pressed', 'false');
          btn.classList.remove('is-selected');
        });
      }
    }

    // Update state
    this.selectedOptions[optionIndex] = optionValue;

    // Update selected value display
    const selectedValueEl = this.container.querySelector(`[data-selected-value-${optionIndex}]`);
    if (selectedValueEl) {
      selectedValueEl.textContent = displayValue;
      selectedValueEl.classList.add('has-selection');
    }

    // Update product title with selected variant (e.g., "Product Title - Red Replica")
    this.updateProductTitle();

    // Apply conditional visibility based on selection
    this.applyConditionalVisibility();

    // Update variant availability
    this.updateVariantAvailability();

    // Update summary
    this.updateSummary();

    // Check if this should trigger direct selection (add to cart)
    if (this.shouldDirectSelect(optionIndex, optionValue)) {
      this.handleDirectSelect();
      return;
    }

    // Always try to find and select a matching variant (even if not all options selected)
    // This allows showing product info immediately when first option is selected
    this.findAndSelectVariant();

    // Auto-select next option if only 1 choice available (for products with 1 edition number)
    this.autoSelectSingleOption(optionIndex);

    // Scroll to next option on mobile (but not during initialization to prevent page jumping)
    if (window.innerWidth <= 768 && !this.isInitializing) {
      const nextGroup = group.nextElementSibling;
      if (nextGroup && nextGroup.dataset.optionGroup !== undefined && !nextGroup.hidden) {
        nextGroup.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
      }
    }
  }

  handleSearch(event, optionIndex) {
    const searchTerm = event.target.value.toLowerCase().trim();
    const group = this.container.querySelector(`[data-option-index="${optionIndex}"]`);
    const buttons = group.querySelectorAll('[data-option-button]');
    const noResultsEl = group.querySelector(`[data-no-results-${optionIndex}]`);

    let hasVisibleResults = false;

    buttons.forEach(button => {
      const value = (button.dataset.optionValue || '').toLowerCase();
      const displayValue = (button.dataset.displayValue || '').toLowerCase();

      const isMatch = value.includes(searchTerm) || displayValue.includes(searchTerm);

      button.style.display = isMatch ? '' : 'none';
      if (isMatch && !button.disabled) {
        hasVisibleResults = true;
      }
    });

    if (noResultsEl) {
      noResultsEl.hidden = hasVisibleResults || searchTerm.length === 0;
    }
  }

  updateVariantAvailability() {
    const currentProductUrl = this.productSection?.dataset.productUrl;

    this.optionGroups.forEach(group => {
      if (group.hidden || group.style.display === 'none') return;

      const optionIndex = parseInt(group.dataset.optionIndex);
      const buttons = group.querySelectorAll('[data-option-button]');

      buttons.forEach(button => {
        button.disabled = false;
        button.classList.remove('is-unavailable', 'is-nonexistent');
        button.removeAttribute('data-unavailable');
        button.style.display = ''; // Reset display

        // Skip availability check for buttons that point to different products (combined listings)
        // These will trigger a product swap, so they should always be enabled
        const buttonProductUrl = button.dataset.productUrl;
        if (buttonProductUrl && buttonProductUrl !== currentProductUrl) {
          return; // Keep button enabled, skip availability check
        }

        const optionValue = button.dataset.optionValue;
        const testOptions = { ...this.selectedOptions };
        testOptions[optionIndex] = optionValue;

        // Check if ANY variant exists with this combination (regardless of availability)
        const variantExists = this.variants.some(variant => {
          for (let i = 0; i < this.numberOfOptions; i++) {
            const optionGroup = this.container.querySelector(`[data-option-group][data-option-index="${i}"]`);
            const isGroupVisible = optionGroup && !optionGroup.hidden && optionGroup.style.display !== 'none';
            if (isGroupVisible && i <= optionIndex && testOptions[i] !== undefined && variant.options[i] !== testOptions[i]) {
              return false;
            }
          }
          return true; // Variant exists with this option combination
        });

        // Check if variant is available (exists AND in stock)
        const hasMatchingVariant = this.variants.some(variant => {
          for (let i = 0; i < this.numberOfOptions; i++) {
            const optionGroup = this.container.querySelector(`[data-option-group][data-option-index="${i}"]`);
            const isGroupVisible = optionGroup && !optionGroup.hidden && optionGroup.style.display !== 'none';
            if (isGroupVisible && i <= optionIndex && testOptions[i] !== undefined && variant.options[i] !== testOptions[i]) {
              return false;
            }
          }
          return variant.available && !variant.blocked_reason;
        });

        if (!variantExists) {
          // No variant exists at all - hide the button completely
          button.disabled = true;
          button.classList.add('is-nonexistent');
          button.style.display = 'none';
        } else if (!hasMatchingVariant) {
          // Variant exists but sold out - grey out
          button.disabled = true;
          button.classList.add('is-unavailable');
          button.setAttribute('data-unavailable', 'true');
        }
      });
    });
  }

  findAndSelectVariant() {
    // Build options array from selected values (only visible groups)
    const optionsArray = [];
    for (let i = 0; i < this.numberOfOptions; i++) {
      const optionGroup = this.container.querySelector(`[data-option-group][data-option-index="${i}"]`);
      const isVisible = optionGroup && !optionGroup.hidden && optionGroup.style.display !== 'none';

      if (isVisible) {
        optionsArray[i] = this.selectedOptions[i];
      }
    }

    // Find matching variants - only match on visible option indices
    const matchingVariants = this.variants.filter(variant => {
      for (let i = 0; i < this.numberOfOptions; i++) {
        const optionGroup = this.container.querySelector(`[data-option-group][data-option-index="${i}"]`);
        const isVisible = optionGroup && !optionGroup.hidden && optionGroup.style.display !== 'none';

        if (isVisible && optionsArray[i] && variant.options[i] !== optionsArray[i]) {
          return false;
        }
      }
      return true;
    });

    // Prefer available variants over unavailable ones
    let matchingVariant = matchingVariants.find(v => v.available && !v.blocked_reason);
    if (!matchingVariant && matchingVariants.length > 0) {
      matchingVariant = matchingVariants[0]; // Fallback to first variant if none available
    }

    if (matchingVariant) {
      this.currentVariant = matchingVariant;

      // Update hidden input
      if (this.variantIdInput) {
        this.variantIdInput.value = matchingVariant.id;
      }

      // Update all product information (price, SKU, images, etc.)
      this.updatePrice();
      this.updateSku();
      this.updateBarcode();
      this.updateWeight();
      this.updateMedia();

      // Enable add to cart if available
      if (matchingVariant.available && !matchingVariant.blocked_reason) {
        this.updateAddToCartButton(true, matchingVariant);
      } else {
        this.updateAddToCartButton(false, matchingVariant);
      }

      // Dispatch event for product form to update price, image, SKU, etc.
      this.dispatchVariantSelectedEvent(matchingVariant);

      this.updateURL(matchingVariant.id);
    }
  }

  autoSelectSingleOption(currentOptionIndex) {
    const nextOptionIndex = currentOptionIndex + 1;
    if (nextOptionIndex >= this.numberOfOptions) return;

    const nextGroup = this.container.querySelector(`[data-option-group][data-option-index="${nextOptionIndex}"]`);
    if (!nextGroup || nextGroup.hidden || nextGroup.style.display === 'none') return;

    const currentSelections = [];
    for (let i = 0; i <= currentOptionIndex; i++) {
      currentSelections[i] = this.selectedOptions[i];
    }

    const matchingVariants = this.variants.filter(variant => {
      for (let i = 0; i <= currentOptionIndex; i++) {
        if (currentSelections[i] && variant.options[i] !== currentSelections[i]) {
          return false;
        }
      }
      return variant.available && !variant.blocked_reason;
    });

    if (matchingVariants.length === 1) {
      const autoSelectValue = matchingVariants[0].options[nextOptionIndex];
      const buttons = nextGroup.querySelectorAll('[data-option-button]');
      buttons.forEach(btn => {
        if (btn.dataset.optionValue === autoSelectValue) btn.click();
      });
    }
  }

  allOptionsSelected() {
    // Only check VISIBLE option groups
    for (let i = 0; i < this.numberOfOptions; i++) {
      const optionGroup = this.container.querySelector(`[data-option-group][data-option-index="${i}"]`);
      const isVisible = optionGroup && !optionGroup.hidden && optionGroup.style.display !== 'none';

      // If group is visible and not selected, return false
      if (isVisible && !this.selectedOptions[i]) {
        return false;
      }
    }
    return true;
  }

  updateSummary() {
    if (!this.summaryEl) return;

    const contentEl = this.summaryEl.querySelector('[data-summary-content]');
    const actionBtn = this.summaryEl.querySelector('[data-summary-action]');
    const actionText = this.summaryEl.querySelector('[data-summary-action-text]');
    if (!contentEl) return;

    // Remove all state classes
    this.summaryEl.classList.remove('is-complete', 'is-unavailable', 'needs-selection');
    if (actionBtn) actionBtn.style.display = 'none';

    if (!this.allOptionsSelected()) {
      // Find which visible options are not selected
      let visibleCount = 0;
      let selectedVisibleCount = 0;
      let firstUnselectedGroup = null;
      let firstUnselectedName = '';

      for (let i = 0; i < this.numberOfOptions; i++) {
        const optionGroup = this.container.querySelector(`[data-option-group][data-option-index="${i}"]`);
        const isVisible = optionGroup && !optionGroup.hidden && optionGroup.style.display !== 'none';

        if (isVisible) {
          visibleCount++;
          if (this.selectedOptions[i]) {
            selectedVisibleCount++;
          } else if (!firstUnselectedGroup) {
            firstUnselectedGroup = optionGroup;
            firstUnselectedName = optionGroup.dataset.optionName || 'option';
          }
        }
      }

      const remaining = visibleCount - selectedVisibleCount;

      // Check if first option selected but subsequent option needs selection (e.g., Limited Edition Number)
      if (selectedVisibleCount > 0 && firstUnselectedGroup) {
        this.summaryEl.classList.add('needs-selection');
        contentEl.innerHTML = `<strong>Almost there!</strong> Please select your ${firstUnselectedName}`;

        if (actionBtn && actionText) {
          actionText.textContent = `Select ${firstUnselectedName} ↑`;
          actionBtn.style.display = 'flex';

          // Remove old listener and add new one
          actionBtn.onclick = () => {
            firstUnselectedGroup.scrollIntoView({ behavior: 'smooth', block: 'center' });
            // Flash the group to draw attention
            firstUnselectedGroup.style.transition = 'box-shadow 0.3s ease';
            firstUnselectedGroup.style.boxShadow = '0 0 0 3px #c8102e';
            setTimeout(() => {
              firstUnselectedGroup.style.boxShadow = '';
            }, 1500);
          };
        }
      } else if (selectedVisibleCount === 0) {
        contentEl.textContent = 'Please select your options above';
      } else {
        contentEl.textContent = `${remaining} more option${remaining !== 1 ? 's' : ''} to select`;
      }
    } else {
      // All visible options selected - hide the summary section
      // (the frame addon order summary shows this info instead)
      this.summaryEl.style.display = 'none';
      return;
    }

    // Show the summary section (only visible when options need to be selected)
    this.summaryEl.style.display = '';
  }

  updateAddToCartButton(enable, variant = null) {
    const addToCartButton = document.querySelector('[data-add-to-cart-text], [name="add"]');
    const buyButtons = document.querySelector('.product-info__buy-buttons');
    if (!addToCartButton) return;

    // Check if all visible options are selected
    const allSelected = this.allOptionsSelected();

    if (enable && variant && allSelected) {
      // Show and enable button
      if (buyButtons) buyButtons.style.display = '';
      addToCartButton.style.display = '';
      addToCartButton.disabled = false;
      const defaultText = addToCartButton.dataset.addToCartText || 'Add to Cart';
      addToCartButton.textContent = defaultText;

      // Dispatch event so frame-addon knows variant is ready
      this.container.dispatchEvent(new CustomEvent('variant:ready', {
        bubbles: true,
        detail: { variant, allOptionsSelected: true }
      }));
    } else {
      // Hide button completely when options not selected
      if (!allSelected) {
        if (buyButtons) buyButtons.style.display = 'none';
        addToCartButton.style.display = 'none';
      } else {
        // Show but disable for sold out/blocked variants
        if (buyButtons) buyButtons.style.display = '';
        addToCartButton.style.display = '';
        addToCartButton.disabled = true;

        if (variant && variant.blocked_reason) {
          addToCartButton.textContent = variant.blocked_reason;
        } else if (variant && !variant.available) {
          addToCartButton.textContent = 'Sold Out';
        }
      }

      // Dispatch event so frame-addon knows variant is not ready
      this.container.dispatchEvent(new CustomEvent('variant:ready', {
        bubbles: true,
        detail: { variant: null, allOptionsSelected: false }
      }));
    }
  }

  formatPrice(cents) {
    const dollars = cents / 100;
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD'
    }).format(dollars);
  }

  dispatchVariantSelectedEvent(variant) {
    // Get product section and form
    const section = document.querySelector('.js-product');
    const productForm = section?.querySelector('.js-product-form-main');

    // Dispatch the SAME event that variant-picker.js uses - this triggers all price/image/SKU updates
    if (section) {
      section.dispatchEvent(new CustomEvent('on:variant:change', {
        bubbles: true,
        detail: {
          form: productForm,
          variant: variant,
          product: { id: this.productId }
        }
      }));
    }

    // Legacy events for compatibility
    this.container.dispatchEvent(new CustomEvent('variant:selected', {
      bubbles: true,
      detail: {
        variantId: variant.id,
        variant: variant,
        selectedOptions: this.selectedOptions
      }
    }));

    document.dispatchEvent(new CustomEvent('variant:changed', {
      detail: {
        variantId: variant.id,
        variant: variant
      }
    }));

    // Trigger change event on the hidden input
    if (this.variantIdInput) {
      const changeEvent = new Event('change', { bubbles: true });
      this.variantIdInput.dispatchEvent(changeEvent);
    }
  }

  updateURL(variantId) {
    if (window.history.replaceState) {
      const url = new URL(window.location);
      url.searchParams.set('variant', variantId);
      window.history.replaceState({}, '', url);
    }
  }

  // Product information update methods
  updatePrice() {
    if (!this.currentVariant || !this.productSection) return;

    const priceContainer = this.productSection.querySelector('.product-info__price > .price');
    if (!priceContainer) return;

    const formattedData = this.formatted[this.currentVariant.id];
    if (!formattedData) return;

    // Update current price and compare at price
    const priceCurrentEl = priceContainer.querySelector('.price__current');
    const priceWasEl = priceContainer.querySelector('.price__was');

    if (priceCurrentEl) {
      priceCurrentEl.innerHTML = formattedData.price;
      // Make sure parent is visible
      priceContainer.classList.remove('hidden');
      priceContainer.style.display = '';
    }

    if (priceWasEl) {
      if (this.currentVariant.compare_at_price && this.currentVariant.compare_at_price > this.currentVariant.price) {
        priceWasEl.innerHTML = formattedData.compareAtPrice || '';
        priceWasEl.classList.remove('hidden');
      } else {
        priceWasEl.innerHTML = '';
        priceWasEl.classList.add('hidden');
      }
    }
  }

  updateSku() {
    if (!this.currentVariant || !this.productSection) return;

    const skuElement = this.productSection.querySelector('.product-sku__value');
    if (!skuElement) return;

    const skuAvailable = this.currentVariant && this.currentVariant.sku;
    skuElement.textContent = skuAvailable ? this.currentVariant.sku : '';
    skuElement.parentNode.hidden = !skuAvailable;
  }

  updateBarcode() {
    if (!this.currentVariant || !this.productSection) return;

    const barcodeElement = this.productSection.querySelector('[data-barcode]');
    if (!barcodeElement) return;

    if (this.currentVariant.barcode) {
      barcodeElement.textContent = this.currentVariant.barcode;
      barcodeElement.parentElement?.classList.remove('visibility-hidden');
    } else {
      barcodeElement.parentElement?.classList.add('visibility-hidden');
    }
  }

  updateWeight() {
    if (!this.currentVariant || !this.productSection) return;

    const weightElement = this.productSection.querySelector('[data-weight]');
    if (!weightElement) return;

    const formattedData = this.formatted[this.currentVariant.id];
    if (!formattedData || !formattedData.weight) {
      weightElement.parentElement?.classList.add('visibility-hidden');
      return;
    }

    weightElement.textContent = formattedData.weight;
    weightElement.parentElement?.classList.remove('visibility-hidden');
  }

  updateMedia() {
    if (!this.currentVariant || !this.productSection) return;

    // Find media gallery and trigger media change
    const mediaGallery = this.productSection.querySelector('media-gallery');
    if (!mediaGallery || !this.currentVariant.featured_media?.id) return;

    // Dispatch media change event
    mediaGallery.dispatchEvent(new CustomEvent('variant:media:change', {
      detail: {
        mediaId: this.currentVariant.featured_media.id,
        mediaPosition: this.currentVariant.featured_media.position
      }
    }));
  }

  updateProductTitle() {
    if (!this.titleElement || !this.originalTitle) return;

    // Get the first selected option (typically the main variant like Jersey Type)
    const firstOptionValue = this.selectedOptions[0];

    if (firstOptionValue) {
      // Update title with variant appended: "Product Title (Red Replica)"
      this.titleElement.textContent = `${this.originalTitle} (${firstOptionValue})`;
    } else {
      // Reset to original title if no option selected
      this.titleElement.textContent = this.originalTitle;
    }
  }

  checkURLVariant() {
    const urlParams = new URLSearchParams(window.location.search);
    const variantId = urlParams.get('variant');

    if (variantId) {
      let variant = this.variants.find(v => v.id.toString() === variantId);

      // If URL variant is sold out or blocked, find first available variant with same first option
      if (variant && (!variant.available || variant.blocked_reason)) {
        const firstOptionValue = variant.options[0];

        // Try to find an available variant with the same first option (e.g., same Jersey Type)
        const availableWithSameOption = this.variants.find(v =>
          v.options[0] === firstOptionValue &&
          v.available &&
          !v.blocked_reason
        );

        if (availableWithSameOption) {
          variant = availableWithSameOption;
          // Update URL to reflect the available variant
          this.updateURL(variant.id);
        } else {
          // Fall back to any available variant
          const anyAvailable = this.variants.find(v => v.available && !v.blocked_reason);
          if (anyAvailable) {
            variant = anyAvailable;
            this.updateURL(variant.id);
          }
        }
      }

      if (variant) {
        // Select all options for this variant
        variant.options.forEach((optionValue, index) => {
          const group = this.container.querySelector(`[data-option-group][data-option-index="${index}"]`);
          if (group) {
            const button = group.querySelector(
              `[data-option-button][data-option-value="${optionValue}"]`
            );
            if (button) {
              button.click();
            }
          }
        });

        // Update product title after URL variant is loaded
        this.updateProductTitle();
      }
    }
  }

  // Schema-based configuration methods
  validateConfig() {
    if (!this.config || typeof this.config !== 'object') {
      this.config = null;
      return false;
    }

    if (!this.config.conditionalOptions) {
      return false;
    }

    return true;
  }

  applyConditionalVisibility() {
    // ALWAYS reset all option groups to visible first
    this.optionGroups.forEach(group => {
      group.hidden = false;
      group.style.display = '';
    });

    if (!this.config || !this.config.conditionalOptions) return;

    const conditionalOptions = this.config.conditionalOptions;

    // Check each selected option to see if it has conditional rules
    for (let optionIndex = 0; optionIndex < this.numberOfOptions; optionIndex++) {
      const selectedValue = this.selectedOptions[optionIndex];
      if (!selectedValue) continue;

      const optionName = this.optionNames[optionIndex];
      const optionRules = conditionalOptions[optionName];
      if (!optionRules || !optionRules[selectedValue]) continue;

      const rules = optionRules[selectedValue];

      // Handle hideOptions
      if (rules.hideOptions && Array.isArray(rules.hideOptions)) {
        rules.hideOptions.forEach(optionToHide => {
          const groupToHide = this.findOptionGroupByName(optionToHide);
          if (groupToHide) {
            groupToHide.hidden = true;
            groupToHide.style.display = 'none';
          }
        });
      }

      // Handle showOptions
      if (rules.showOptions && Array.isArray(rules.showOptions)) {
        rules.showOptions.forEach(optionToShow => {
          const groupToShow = this.findOptionGroupByName(optionToShow);
          if (groupToShow) {
            groupToShow.hidden = false;
            groupToShow.style.display = '';
          }
        });
      }
    }
  }

  shouldDirectSelect(optionIndex, optionValue) {
    if (!this.config || !this.config.conditionalOptions) {
      return false;
    }

    const optionName = this.optionNames[optionIndex];
    const conditionalOptions = this.config.conditionalOptions;
    const optionRules = conditionalOptions[optionName];

    if (!optionRules || !optionRules[optionValue]) {
      return false;
    }

    const rules = optionRules[optionValue];

    // Check if directSelect is enabled and all visible options are selected
    if (rules.directSelect === true) {
      // Count how many options are visible and selected
      let visibleOptions = 0;
      let selectedVisibleOptions = 0;

      this.optionGroups.forEach((group, idx) => {
        if (!group.hidden) {
          visibleOptions++;
          if (this.selectedOptions[idx]) {
            selectedVisibleOptions++;
          }
        }
      });

      // Direct select if all visible options are selected
      return visibleOptions === selectedVisibleOptions;
    }

    return false;
  }

  handleDirectSelect() {
    // Find the matching variant
    this.findAndSelectVariant();
  }

  findOptionGroupByName(optionName) {
    for (let i = 0; i < this.optionNames.length; i++) {
      if (this.optionNames[i] === optionName) {
        return this.container.querySelector(`[data-option-group][data-option-index="${i}"]`);
      }
    }
    return null;
  }

  // Public API
  reset() {
    this.selectedOptions = {};

    // Reset all buttons
    this.container.querySelectorAll('[data-option-button]').forEach(button => {
      button.setAttribute('aria-pressed', 'false');
      button.classList.remove('is-selected');
      button.disabled = false;
      button.classList.remove('is-unavailable');
    });

    // Reset selected value displays
    this.container.querySelectorAll('[class*="selected-value"]').forEach(el => {
      if (el.dataset.selectedValue !== undefined) {
        el.textContent = 'Not selected';
        el.classList.remove('has-selection');
      }
    });

    // Clear search inputs
    this.container.querySelectorAll('[data-search-input]').forEach(input => {
      input.value = '';
      const event = new Event('input');
      input.dispatchEvent(event);
    });

    // Reset hidden input
    if (this.variantIdInput) {
      this.variantIdInput.value = '';
    }

    // Reset product title to original
    if (this.titleElement && this.originalTitle) {
      this.titleElement.textContent = this.originalTitle;
    }

    // Reset visibility
    this.applyConditionalVisibility();

    // Update UI
    this.updateSummary();
    this.updateAddToCartButton(false);
  }

  /**
   * Handles Combined Listings product swap using Section Rendering API
   * Fetches new product content via AJAX and replaces the section without page reload
   * @param {string} newProductUrl - URL of the new product to swap to
   * @param {HTMLElement} button - The button that triggered the swap
   */
  handleCombinedListingsSwap(newProductUrl, button) {
    // Find the main product section to replace
    const productSection = document.querySelector('.mbp-product-section, [data-section-type="mystery-box-page"]');
    if (!productSection) {
      window.location.href = newProductUrl;
      return;
    }

    // Get section ID for Section Rendering API
    let sectionId = productSection.dataset.sectionId;
    if (!sectionId) {
      const shopifyWrapper = productSection.closest('[id^="shopify-section-"]');
      if (shopifyWrapper) {
        sectionId = shopifyWrapper.id.replace('shopify-section-', '');
      }
    }

    // Show loading state
    button.classList.add('is-loading');
    productSection.style.opacity = '0.6';
    productSection.style.pointerEvents = 'none';

    // Build fetch URL with section_id parameter for Section Rendering API
    const fetchUrl = sectionId
      ? `${newProductUrl}?section_id=${sectionId}`
      : newProductUrl;

    fetch(fetchUrl)
      .then(response => {
        if (!response.ok) throw new Error('Network response was not ok');
        return response.text();
      })
      .then(html => {
        // Parse the response HTML
        const parser = new DOMParser();
        const doc = parser.parseFromString(html, 'text/html');

        // Find the new section content
        let newSection = doc.querySelector('.mbp-product-section, [data-section-type="mystery-box-page"]');

        // If section_id was used, the response might be just the section content
        if (!newSection && sectionId) {
          newSection = doc.body.firstElementChild || doc.body;
        }

        if (newSection) {
          // IMPORTANT: Replace the ENTIRE element using outerHTML to avoid nesting issues
          // The Section Rendering API returns the full section wrapper, so we need to replace
          // the existing wrapper entirely, not just its contents

          // Store parent reference before replacement
          const parent = productSection.parentNode;
          const nextSibling = productSection.nextSibling;

          // Create a new element from the fetched content
          const tempContainer = document.createElement('div');
          tempContainer.innerHTML = newSection.outerHTML;
          const newElement = tempContainer.firstElementChild;

          // Copy over essential data attributes that might differ
          if (newSection.dataset.productUrl) {
            newElement.dataset.productUrl = newSection.dataset.productUrl;
          }

          // Replace the old section with the new one
          if (nextSibling) {
            parent.insertBefore(newElement, nextSibling);
          } else {
            parent.appendChild(newElement);
          }
          productSection.remove();

          // Update document title from new product
          const newTitle = doc.querySelector('title');
          if (newTitle) {
            document.title = newTitle.textContent;
          }

          // Execute inline scripts from the full response
          // Scripts inserted via innerHTML/DOMParser don't auto-execute
          // Query from doc.body because main script is sibling of mbp-product-section
          const scripts = Array.from(doc.body.querySelectorAll('script'));

          // Clean up previously injected scripts to prevent memory leaks
          document.querySelectorAll('script[data-mbp-injected]').forEach(s => s.remove());

          // Process scripts after microtask to ensure DOM is updated
          Promise.resolve().then(() => {
            scripts.forEach((oldScript) => {
              // Skip external scripts - already loaded globally
              if (oldScript.src) {
                oldScript.remove();
                return;
              }

              // Skip non-JavaScript scripts (JSON-LD, etc.)
              const scriptType = oldScript.type?.toLowerCase() || '';
              if (scriptType && scriptType !== 'text/javascript' && scriptType !== 'module') {
                return;
              }

              // Skip empty or JSON-like scripts
              const scriptContent = oldScript.textContent.trim();
              if (!scriptContent || scriptContent.startsWith('{') || scriptContent.startsWith('[')) {
                return;
              }

              try {
                const newScript = document.createElement('script');
                newScript.type = 'text/javascript';
                newScript.dataset.mbpInjected = 'true';
                newScript.textContent = scriptContent;
                oldScript.remove();
                document.body.appendChild(newScript);
              } catch (err) {
                console.error('Error executing section script:', err);
              }
            });
          });

          // Re-initialize the variant picker for the new product
          const newPicker = newElement.querySelector('[data-multi-option-picker]');
          if (newPicker) {
            new MultiOptionVariantPicker(newPicker);
          }

          // Re-initialize media gallery if present
          const mediaGallery = newElement.querySelector('media-gallery');
          if (mediaGallery && typeof mediaGallery.connectedCallback === 'function') {
            mediaGallery.connectedCallback();
          }

          // Update browser URL without reload
          const newUrl = new URL(newProductUrl, window.location.origin);
          window.history.pushState({ combinedListing: true }, '', newUrl.pathname);

          // Dispatch event for any listeners (analytics, etc.)
          document.dispatchEvent(new CustomEvent('combined-listing:swapped', {
            detail: { newProductUrl, sectionId, newElement }
          }));

          // Trigger Shopify's section:load event for theme compatibility
          document.dispatchEvent(new CustomEvent('shopify:section:load', {
            detail: { sectionId }
          }));

          // Remove loading state from new element
          newElement.style.opacity = '';
          newElement.style.pointerEvents = '';
        } else {
          // Fallback: navigate to new page
          window.location.href = newProductUrl;
        }
      })
      .catch(() => {
        // Fallback to full page navigation on error
        window.location.href = newProductUrl;
      })
      .finally(() => {
        // Remove loading state (in case of error before replacement)
        // Check if button still exists in DOM (might be in swapped section)
        if (button.isConnected) {
          button.classList.remove('is-loading');
        }
        if (productSection.isConnected) {
          productSection.style.opacity = '';
          productSection.style.pointerEvents = '';
        }
      });
  }
}

// Auto-initialize
function initMultiOptionVariantPickers() {
  const pickers = document.querySelectorAll('[data-multi-option-picker]');
  const instances = [];

  // Check if we should restore scroll position
  let shouldRestoreScroll = false;
  try {
    if (sessionStorage.getItem('mbp_preserve_scroll') === 'true') {
      shouldRestoreScroll = true;
      sessionStorage.removeItem('mbp_preserve_scroll');
    }
  } catch (e) {}

  pickers.forEach(picker => {
    const instance = new MultiOptionVariantPicker(picker);
    instances.push(instance);
    picker.multiOptionVariantPicker = instance;
  });

  // Restore scroll position if needed
  if (shouldRestoreScroll && instances.length > 0) {
     // Scroll to the first picker
     setTimeout(() => {
        const picker = pickers[0];
        if (picker) {
            picker.scrollIntoView({ behavior: 'auto', block: 'center' });
        }
     }, 100);
  }
}

// Initialize on DOM ready
if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', initMultiOptionVariantPickers);
} else {
  initMultiOptionVariantPickers();
}

// Handle browser back/forward navigation for combined listings
window.addEventListener('popstate', (event) => {
  if (event.state?.combinedListing) {
    // Reload page to get correct product content
    window.location.reload();
  }
});

// Export
if (typeof window !== 'undefined') {
  window.MultiOptionVariantPicker = MultiOptionVariantPicker;
}

} // End of guard: if (typeof MultiOptionVariantPicker === 'undefined')
